import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Pagination,
  Rating,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import CreateIcon from "@mui/icons-material/Create";
import EditIcon from "@mui/icons-material/Edit";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReviewCard from "./ReviewCard";
import { getDateFromCreatedAt } from "../../../utils/dateUtils";

const REVIEWS_PER_PAGE = 4;

const CustomerReviews = ({
  productReviewsData = {},
  allReviews = [],
  reviewByUser = null,
  onWriteReview = null,
  product = null,
  productId = null,
}) => {
  const [selectedRatingFilter, setSelectedRatingFilter] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);

  // Ratings calculation
  const ratings = useMemo(
    () =>
      Array.isArray(productReviewsData?.ratings)
        ? productReviewsData.ratings
        : [],
    [productReviewsData?.ratings]
  );
  const totalRatings = ratings.length;

  const starCounts = useMemo(() => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    ratings.forEach((num) => {
      const star = Math.max(1, Math.min(5, Math.round(Number(num))));
      if (counts[star] !== undefined) {
        counts[star]++;
      }
    });
    return counts;
  }, [ratings]);

  const starPercentages = useMemo(() => {
    const p = {};
    [1, 2, 3, 4, 5].forEach((star) => {
      p[star] = totalRatings > 0 ? Math.round((starCounts[star] / totalRatings) * 100) : 0;
    });
    return p;
  }, [starCounts, totalRatings]);

  const avgRating = useMemo(() => {
    if (typeof productReviewsData?.avg === "number" && productReviewsData.avg > 0) {
      return Number(productReviewsData.avg.toFixed(1));
    }
    if (totalRatings > 0) {
      const sum = ratings.reduce((acc, curr) => acc + Number(curr), 0);
      return Number((sum / totalRatings).toFixed(1));
    }
    return 0;
  }, [productReviewsData?.avg, ratings, totalRatings]);

  const recommendedPercentage = useMemo(() => {
    if (totalRatings === 0) return 0;
    const positive = (starCounts[4] || 0) + (starCounts[5] || 0);
    return Math.round((positive / totalRatings) * 100);
  }, [starCounts, totalRatings]);

  // Product reviews matching
  const proReviewsArr = useMemo(() => {
    if (!allReviews || !Array.isArray(allReviews)) return [];
    const reviewIds = new Set(productReviewsData?.reviews || []);
    const targetProductId = product?.id || productId;

    const matched = allReviews.filter((item) => {
      if (!item) return false;
      return (
        reviewIds.has(item.id) ||
        (targetProductId &&
          (item.product_id === targetProductId || item.productId === targetProductId))
      );
    });

    // Remove duplicates by id
    const uniqueMap = new Map();
    matched.forEach((r) => {
      if (r && r.id) uniqueMap.set(r.id, r);
    });
    return Array.from(uniqueMap.values());
  }, [allReviews, productReviewsData?.reviews, product?.id, productId]);

  // Count reviews per star
  const reviewsStarCounts = useMemo(() => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    proReviewsArr.forEach((r) => {
      const star = Math.max(1, Math.min(5, Math.round(Number(r.rating))));
      if (counts[star] !== undefined) counts[star]++;
    });
    return counts;
  }, [proReviewsArr]);

  // Filtered & Sorted reviews
  const filteredAndSortedReviews = useMemo(() => {
    let result = [...proReviewsArr];

    if (selectedRatingFilter !== null) {
      result = result.filter(
        (r) => Math.round(Number(r.rating)) === selectedRatingFilter
      );
    }

    result.sort((a, b) => {
      if (sortBy === "highest") {
        return (Number(b.rating) || 0) - (Number(a.rating) || 0);
      }
      if (sortBy === "lowest") {
        return (Number(a.rating) || 0) - (Number(b.rating) || 0);
      }
      // "newest" by default
      const dateA = getDateFromCreatedAt(a.createdAt).getTime();
      const dateB = getDateFromCreatedAt(b.createdAt).getTime();
      return dateB - dateA;
    });

    return result;
  }, [proReviewsArr, selectedRatingFilter, sortBy]);

  const totalPages = Math.ceil(filteredAndSortedReviews.length / REVIEWS_PER_PAGE);

  const paginatedReviews = useMemo(() => {
    const start = (currentPage - 1) * REVIEWS_PER_PAGE;
    return filteredAndSortedReviews.slice(start, start + REVIEWS_PER_PAGE);
  }, [filteredAndSortedReviews, currentPage]);

  const handleFilterToggle = (star) => {
    setSelectedRatingFilter((prev) => (prev === star ? null : star));
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    const reviewsEl = document.getElementById("reviews-list-start");
    if (reviewsEl) {
      reviewsEl.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const getBarColor = (star) => {
    if (star >= 4) return "#075e54"; // primary dark green
    if (star === 3) return "#f59e0b"; // amber
    return "#ef4444"; // red/coral for low ratings
  };

  return (
    <Box id="customer-reviews-section" sx={{ width: "100%", pt: 2, pb: 4 }}>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 800,
            color: "#0f172a",
            letterSpacing: "-0.01em",
            mb: 0.5,
          }}
        >
          Customer Ratings & Reviews
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b" }}>
          Verified ratings and authentic feedback from real customers
        </Typography>
      </Box>

      {/* Ratings Summary & Write Review Card Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Left Side: Score & Distribution */}
        <Grid item xs={12} md={7} lg={8}>
          <Card
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              borderRadius: "16px",
              border: "1px solid rgba(0, 0, 0, 0.08)",
              backgroundColor: "#ffffff",
              boxShadow: "0 2px 12px rgba(7, 94, 84, 0.04)",
            }}
          >
            <Grid container spacing={3} alignItems="center">
              {/* Overall Score */}
              <Grid
                item
                xs={12}
                sm={4}
                sx={{
                  textAlign: { xs: "left", sm: "center" },
                  borderRight: { sm: "1px solid rgba(0, 0, 0, 0.08)" },
                  pr: { sm: 2 },
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    color: "#075e54",
                    lineHeight: 1,
                    mb: 1,
                  }}
                >
                  {avgRating > 0 ? avgRating.toFixed(1) : "0.0"}
                  <Typography
                    component="span"
                    variant="h5"
                    sx={{ color: "#94a3b8", fontWeight: 500, ml: 0.5 }}
                  >
                    / 5
                  </Typography>
                </Typography>

                <Rating
                  name="summary-rating-read-only"
                  value={avgRating}
                  precision={0.1}
                  readOnly
                  size="medium"
                  sx={{
                    color: "#faaf00",
                    mb: 1,
                    "& .MuiRating-iconFilled": {
                      filter: "drop-shadow(0 1px 2px rgba(250, 175, 0, 0.3))",
                    },
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{ color: "#475569", fontWeight: 600, fontSize: "0.85rem" }}
                >
                  {totalRatings} {totalRatings === 1 ? "Rating" : "Ratings"} •{" "}
                  {proReviewsArr.length}{" "}
                  {proReviewsArr.length === 1 ? "Review" : "Reviews"}
                </Typography>

                {totalRatings > 0 && recommendedPercentage > 0 && (
                  <Chip
                    icon={
                      <ThumbUpOutlinedIcon
                        sx={{ fontSize: "14px !important", color: "#075e54 !important" }}
                      />
                    }
                    label={`${recommendedPercentage}% recommend`}
                    size="small"
                    sx={{
                      mt: 1.5,
                      fontWeight: 700,
                      fontSize: "0.74rem",
                      backgroundColor: "rgba(7, 94, 84, 0.08)",
                      color: "#075e54",
                      border: "1px solid rgba(7, 94, 84, 0.2)",
                      borderRadius: "6px",
                    }}
                  />
                )}
              </Grid>

              {/* Star Breakdown Bars */}
              <Grid item xs={12} sm={8}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748b",
                    fontWeight: 600,
                    display: "block",
                    mb: 1.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Rating Breakdown {selectedRatingFilter && "(Click to reset filter)"}
                </Typography>

                <Stack spacing={1.2}>
                  {[5, 4, 3, 2, 1].map((star) => {
                    const isSelected = selectedRatingFilter === star;
                    const percent = starPercentages[star] || 0;
                    const count = starCounts[star] || 0;

                    return (
                      <Box
                        key={star}
                        onClick={() => handleFilterToggle(star)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          p: 0.5,
                          borderRadius: "8px",
                          backgroundColor: isSelected
                            ? "rgba(7, 94, 84, 0.08)"
                            : "transparent",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? "rgba(7, 94, 84, 0.12)"
                              : "rgba(0, 0, 0, 0.03)",
                          },
                        }}
                        title={`Filter by ${star} star reviews (${count})`}
                      >
                        {/* Star label */}
                        <Box
                          sx={{
                            minWidth: 55,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isSelected ? 800 : 600,
                              color: isSelected ? "#075e54" : "#1e293b",
                              fontSize: "0.85rem",
                            }}
                          >
                            {star}
                          </Typography>
                          <StarIcon sx={{ fontSize: 16, color: "#faaf00" }} />
                        </Box>

                        {/* Progress Bar */}
                        <Box sx={{ flexGrow: 1, mx: 1.5 }}>
                          <LinearProgress
                            variant="determinate"
                            value={percent}
                            sx={{
                              height: 10,
                              borderRadius: 5,
                              backgroundColor: "#f1f5f9",
                              "& .MuiLinearProgress-bar": {
                                borderRadius: 5,
                                backgroundColor: getBarColor(star),
                                transition: "transform 0.4s ease",
                              },
                            }}
                          />
                        </Box>

                        {/* Percentage & Count */}
                        <Box sx={{ minWidth: 65, textAlign: "right" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isSelected ? 800 : 600,
                              color: isSelected ? "#075e54" : "#64748b",
                              fontSize: "0.8rem",
                            }}
                          >
                            {percent}%{" "}
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ color: "#94a3b8" }}
                            >
                              ({count})
                            </Typography>
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Right Side: Write / Edit Review CTA */}
        <Grid item xs={12} md={5} lg={4}>
          <Card
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3 },
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              borderRadius: "16px",
              border: "1.5px dashed rgba(7, 94, 84, 0.35)",
              backgroundColor: "rgba(7, 94, 84, 0.02)",
              textAlign: "center",
            }}
          >
            {reviewByUser !== null ? (
              // Current User Has Already Reviewed
              <Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.6,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "20px",
                    backgroundColor: "rgba(22, 163, 74, 0.1)",
                    color: "#15803d",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    mb: 1.5,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                  <span>You reviewed this product</span>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <Rating
                    value={Number(reviewByUser.rating) || 0}
                    readOnly
                    size="small"
                    sx={{ color: "#faaf00" }}
                  />
                  {reviewByUser.headline && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: "#0f172a",
                        mt: 0.5,
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      "{reviewByUser.headline}"
                    </Typography>
                  )}
                  {reviewByUser.detail && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        mt: 0.5,
                      }}
                    >
                      {reviewByUser.detail}
                    </Typography>
                  )}
                </Box>

                <Button
                  onClick={onWriteReview}
                  variant="outlined"
                  fullWidth
                  startIcon={<EditIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 700,
                    py: 1,
                    color: "#075e54",
                    borderColor: "#075e54",
                    "&:hover": {
                      borderColor: "#064e46",
                      backgroundColor: "rgba(7, 94, 84, 0.06)",
                    },
                  }}
                >
                  Edit Your Review
                </Button>
              </Box>
            ) : (
              // Current User Has Not Reviewed Yet
              <Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: "rgba(7, 94, 84, 0.08)",
                    color: "#075e54",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <RateReviewOutlinedIcon sx={{ fontSize: 26 }} />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: "#0f172a",
                    fontSize: "1.1rem",
                    mb: 0.5,
                  }}
                >
                  Have you used this product?
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.85rem",
                    lineHeight: 1.5,
                    mb: 2.5,
                    maxWidth: 280,
                    mx: "auto",
                  }}
                >
                  Share your honest experience and help other shoppers make informed decisions.
                </Typography>

                <Button
                  onClick={onWriteReview}
                  variant="contained"
                  fullWidth
                  startIcon={<CreateIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 700,
                    py: 1.1,
                    backgroundColor: "#075e54",
                    color: "#ffffff",
                    boxShadow: "0 4px 14px rgba(7, 94, 84, 0.25)",
                    "&:hover": {
                      backgroundColor: "#064e46",
                    },
                  }}
                >
                  Write a Review
                </Button>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Reviews List & Controls */}
      <Box id="reviews-list-start" sx={{ mt: 4 }}>
        {/* Filters and Sort Toolbar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 3,
            pb: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
          }}
        >
          {/* Rating filter chips */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                mr: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <FilterListIcon sx={{ fontSize: 18, color: "#075e54" }} />
              Filter:
            </Typography>

            <Chip
              label={`All (${proReviewsArr.length})`}
              clickable
              onClick={() => handleFilterToggle(null)}
              variant={selectedRatingFilter === null ? "filled" : "outlined"}
              size="small"
              sx={{
                fontWeight: 600,
                borderRadius: "20px",
                backgroundColor:
                  selectedRatingFilter === null ? "#075e54" : "transparent",
                color: selectedRatingFilter === null ? "#ffffff" : "#475569",
                borderColor:
                  selectedRatingFilter === null ? "#075e54" : "rgba(0,0,0,0.15)",
                "&:hover": {
                  backgroundColor:
                    selectedRatingFilter === null ? "#064e46" : "rgba(7, 94, 84, 0.06)",
                },
              }}
            />

            {[5, 4, 3, 2, 1].map((star) => {
              const count = reviewsStarCounts[star] || 0;
              const isSelected = selectedRatingFilter === star;

              return (
                <Chip
                  key={star}
                  icon={
                    <StarIcon
                      sx={{
                        fontSize: "14px !important",
                        color: isSelected ? "#faaf00 !important" : "#f59e0b !important",
                      }}
                    />
                  }
                  label={`${star} Star (${count})`}
                  clickable
                  onClick={() => handleFilterToggle(star)}
                  variant={isSelected ? "filled" : "outlined"}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    borderRadius: "20px",
                    backgroundColor: isSelected ? "#075e54" : "transparent",
                    color: isSelected ? "#ffffff" : "#475569",
                    borderColor: isSelected ? "#075e54" : "rgba(0,0,0,0.15)",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? "#064e46"
                        : "rgba(7, 94, 84, 0.06)",
                    },
                  }}
                />
              );
            })}
          </Box>

          {/* Sort Dropdown */}
          {proReviewsArr.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                Sort by:
              </Typography>
              <FormControl size="small">
                <Select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setCurrentPage(1);
                  }}
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "#0f172a",
                    borderRadius: "8px",
                    "& .MuiSelect-select": { py: 0.6, px: 1.5 },
                  }}
                >
                  <MenuItem value="newest">Most Recent</MenuItem>
                  <MenuItem value="highest">Highest Rating</MenuItem>
                  <MenuItem value="lowest">Lowest Rating</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        {/* Filter status banner */}
        {selectedRatingFilter !== null && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1.5,
              mb: 2.5,
              borderRadius: "10px",
              backgroundColor: "rgba(7, 94, 84, 0.06)",
              border: "1px solid rgba(7, 94, 84, 0.15)",
            }}
          >
            <Typography variant="body2" sx={{ color: "#075e54", fontWeight: 600 }}>
              Showing {filteredAndSortedReviews.length} reviews rated{" "}
              {selectedRatingFilter} star{selectedRatingFilter > 1 ? "s" : ""}
            </Typography>
            <Button
              size="small"
              onClick={() => setSelectedRatingFilter(null)}
              sx={{
                textTransform: "none",
                color: "#075e54",
                fontWeight: 700,
                fontSize: "0.8rem",
              }}
            >
              Clear filter
            </Button>
          </Box>
        )}

        {/* Reviews List */}
        {filteredAndSortedReviews.length > 0 ? (
          <Stack spacing={2.5}>
            {paginatedReviews.map((item) => (
              <ReviewCard
                key={item.id}
                review={item}
                isCurrentUser={reviewByUser?.id === item.id}
                onEdit={onWriteReview}
              />
            ))}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  pt: 3,
                  pb: 1,
                }}
              >
                <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                  Showing {(currentPage - 1) * REVIEWS_PER_PAGE + 1}–
                  {Math.min(currentPage * REVIEWS_PER_PAGE, filteredAndSortedReviews.length)} of{" "}
                  {filteredAndSortedReviews.length} reviews
                </Typography>

                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  size="medium"
                  sx={{
                    "& .MuiPaginationItem-root.Mui-selected": {
                      backgroundColor: "#075e54 !important",
                      color: "#ffffff !important",
                      fontWeight: 700,
                    },
                  }}
                />
              </Box>
            )}
          </Stack>
        ) : (
          /* Empty State */
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              px: 2,
              borderRadius: "16px",
              backgroundColor: "#f8fafc",
              border: "1px dashed #cbd5e1",
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                backgroundColor: "rgba(7, 94, 84, 0.08)",
                color: "#075e54",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <RateReviewOutlinedIcon sx={{ fontSize: 32 }} />
            </Box>

            {selectedRatingFilter !== null ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
                  No {selectedRatingFilter}-star reviews found
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
                  There are currently no reviews with this specific rating.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedRatingFilter(null)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                    color: "#075e54",
                    borderColor: "#075e54",
                  }}
                >
                  View All Reviews
                </Button>
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#1e293b", mb: 0.5 }}>
                  No customer reviews yet
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#64748b", maxWidth: 400, mx: "auto", mb: 2.5 }}
                >
                  Be the first to review this product and help other customers in the community!
                </Typography>
                <Button
                  variant="contained"
                  onClick={onWriteReview}
                  startIcon={<CreateIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 700,
                    px: 3,
                    py: 1,
                    backgroundColor: "#075e54",
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#064e46",
                    },
                  }}
                >
                  Write the First Review
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CustomerReviews;
